import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowUpRight, GraduationCap, Globe2, MessageSquare, BadgeCheck } from "lucide-react";

const levels = [
  { n: "01", level: "Beginner", hsk: "HSK 1", cn: "入门", desc: "Pinyin, tones & your first 300 words." },
  { n: "02", level: "Elementary", hsk: "HSK 2", cn: "初级", desc: "Everyday conversation & 600 words." },
  { n: "03", level: "Intermediate", hsk: "HSK 3–4", cn: "中级", desc: "Fluency, grammar depth & reading." },
  { n: "04", level: "Advanced", hsk: "HSK 5–6", cn: "高级", desc: "Academic & professional mastery." },
];

export default function Home() {
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/courses", { params: { category: "language" } }).then((r) => setCourses(r.data)).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="lg:col-span-7 flex flex-col justify-center animate-fade-up">
            <span className="label text-cinnabar mb-6">Aligned to the new HSK 3.0 standard</span>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-ink">
              Learn Mandarin.<br />
              <span className="italic text-cinnabar">Teach</span> it beautifully.
            </h1>
            <p className="mt-8 text-lg text-clay max-w-xl leading-relaxed">
              From your first 你好 to a professional teaching diploma — the Center for Chinese Language
              Pedagogy and Training guides learners and educators across India with rigorous, elegant instruction.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/courses" data-testid="hero-courses-btn"
                className="group inline-flex items-center gap-2 px-7 py-4 bg-ink text-rice font-medium hover:bg-cinnabar transition-colors duration-300">
                Explore Courses <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
              </Link>
              <Link to="/consultation" data-testid="hero-consult-btn"
                className="inline-flex items-center gap-2 px-7 py-4 border border-ink text-ink font-medium hover:bg-ink hover:text-rice transition-colors duration-300">
                Study in China
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative h-[360px] lg:h-full min-h-[420px] border border-black/10">
              <img src="https://images.unsplash.com/photo-1486303954368-398fea0e72cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
                alt="Chinese calligraphy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-cinnabar/10" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-black/10 px-6 py-4 hidden lg:block">
              <p className="font-serif text-4xl text-cinnabar leading-none">6</p>
              <p className="label text-clay mt-1">Structured Programs</p>
            </div>
          </div>
        </div>
      </section>

      {/* LEVELS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="label text-cinnabar">The Journey</span>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Four levels, one path to fluency</h2>
          </div>
          <Link to="/courses" className="text-sm font-medium text-ink hover:text-cinnabar transition-colors inline-flex items-center gap-1">
            View all courses <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-black/10">
          {levels.map((l) => (
            <div key={l.n} data-testid={`level-${l.level.toLowerCase()}`}
              className="group border-r border-b border-black/10 p-8 bg-white hover:bg-ink transition-colors duration-300">
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-xs text-clay group-hover:text-rice/60">{l.n}</span>
                <span className="font-serif text-3xl text-cinnabar group-hover:text-gold transition-colors">{l.cn}</span>
              </div>
              <h3 className="mt-8 text-2xl group-hover:text-rice transition-colors">{l.level}</h3>
              <p className="label text-gold mt-1">{l.hsk}</p>
              <p className="mt-4 text-sm text-clay group-hover:text-rice/70 transition-colors leading-relaxed">{l.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section className="bg-white border-y border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <span className="label text-cinnabar">Enroll today</span>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 mb-12">Language courses</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.slice(0, 4).map((c) => (
              <Link key={c.course_id} to={`/courses/${c.slug}`} data-testid={`home-course-${c.slug}`}
                className="group block border border-black/10 bg-rice hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300">
                <div className="h-40 overflow-hidden border-b border-black/10">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <p className="label text-gold">{c.hsk}</p>
                  <h3 className="text-xl mt-2 group-hover:text-cinnabar transition-colors">{c.title}</h3>
                  <p className="text-xs text-clay mt-2">{c.duration}</p>
                  <p className="font-display font-bold text-ink mt-4">₹{c.price_inr.toLocaleString("en-IN")}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { icon: GraduationCap, t: "Teacher Training", d: "TCFL certificate & diploma programs for Indian educators." },
          { icon: Globe2, t: "Study in China", d: "Guidance on universities, scholarships & applications." },
          { icon: MessageSquare, t: "AI Tutor", d: "Practice Mandarin 24/7 with your personal AI Lǎoshī." },
          { icon: BadgeCheck, t: "HSK 3.0 Aligned", d: "Curriculum mapped to the latest official standard." },
        ].map((p, i) => (
          <div key={i} className="border-l-2 border-cinnabar pl-5">
            <p.icon className="text-cinnabar mb-4" size={26} />
            <h3 className="text-xl">{p.t}</h3>
            <p className="text-sm text-clay mt-2 leading-relaxed">{p.d}</p>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-ink text-rice grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl text-rice tracking-tight">Ready to begin your Mandarin journey?</h2>
            <p className="text-rice/60 mt-4 max-w-xl">Join learners and future teachers across India. Create your account and start today.</p>
          </div>
          <Link to="/register" data-testid="cta-register-btn"
            className="px-8 py-4 bg-gold text-ink font-semibold hover:bg-rice transition-colors duration-300 whitespace-nowrap">
            Create Account
          </Link>
        </div>
      </section>
    </div>
  );
}
